// src/components/intake/IntakeShare.js
//
// Ticket 070 (Advisor Intake I8). The share screen: the client shares their SAVED
// results with the advisor linked to their record, or stops sharing. Rendered by
// IntakePage inside the same IntakeShell (rule 12); one component, the state
// (shared or not) only switches the JSX inside it.
//
// Nothing is sent until the button. The advisor's name comes from the server
// (status.advisorName); the browser sends no advisor, no email and no consent text.
// The email shown is the verified account email, locked; the server takes it from
// the token. The consent sentence is the shared template (sharedOptions.js) filled
// with the advisor's name; the server stores the same sentence.
// All wording is in intakeConfig.js.

import React, { useState } from 'react'
import { PHONE_COUNTRIES, DEFAULT_COUNTRY_CODE, getCountry, validatePhoneForCountry } from '../../lib/phoneCountries'
import {
  shareConsentSentence, shareDateText, SHARE_TITLE, SHARE_NOTHING_YET, SHARE_SCOPE_LINE, SHARE_NAME_LABEL,
  SHARE_PHONE_LABEL, SHARE_EMAIL_LABEL, SHARE_EMAIL_LOCKED_HINT, SHARE_PRIVACY_LINK_LABEL, SHARE_ENTRY_LABEL,
  SHARE_SENDING_LABEL, SHARE_BACK_LABEL, SHARE_NAME_MAX, SHARE_STOP_LABEL, SHARE_STOP_CONFIRM_LINE,
  SHARE_STOP_CONFIRM_LABEL, SHARE_STOP_CANCEL_LABEL, SHARE_STOPPING_LABEL, SHARE_ENTRY_SHARED,
  SHARE_ENTRY_LATER_SAVES, SHARE_ERRORS,
} from '../../lib/intake/intakeConfig'

// Digits only, with the dial code ("Other" has none), as the Connect form builds it.
function buildPhone(countryCode, digits) {
  if (!digits) return ''
  return countryCode === 'OTHER' ? digits : `${getCountry(countryCode).dialCode}${digits}`
}

export default function IntakeShare({ status, email, sending, error, onShare, onStop, onBack }) {
  const [name, setName] = useState('')
  const [country, setCountry] = useState(DEFAULT_COUNTRY_CODE)
  const [digits, setDigits] = useState('')
  const [confirmingStop, setConfirmingStop] = useState(false)

  const advisor = status.advisorName
  const phoneCheck = validatePhoneForCountry(country, digits)
  const trimmed = name.trim()
  const canShare = !sending && trimmed.length > 0 && trimmed.length <= SHARE_NAME_MAX && phoneCheck.ok
  const errorText = error ? (SHARE_ERRORS[error] || SHARE_ERRORS.DEFAULT) : null

  function submit(e) {
    e.preventDefault()
    if (!canShare) return
    const contact = { name: trimmed }
    const phone = buildPhone(country, digits)
    if (phone) contact.phone = phone
    onShare(contact)
  }

  if (status.shared) {
    return (
      <div className="intake-share">
        <h1 className="triage-question">{SHARE_TITLE(advisor)}</h1>
        <p className="intake-results-status" role="status">
          {SHARE_ENTRY_SHARED(advisor, shareDateText(status.sharedAt))}
        </p>
        <p className="triage-hint">{SHARE_ENTRY_LATER_SAVES(advisor)}</p>
        {errorText && <p className="intake-limit-note" role="alert">{errorText}</p>}
        {confirmingStop ? (
          <div className="intake-share-confirm">
            <p className="intake-notice">{SHARE_STOP_CONFIRM_LINE(advisor)}</p>
            <div className="intake-actions">
              <button type="button" className="btn-primary" disabled={sending} onClick={onStop}>
                {sending ? SHARE_STOPPING_LABEL : SHARE_STOP_CONFIRM_LABEL}
              </button>
              <button type="button" className="btn-secondary" disabled={sending} onClick={() => setConfirmingStop(false)}>
                {SHARE_STOP_CANCEL_LABEL}
              </button>
            </div>
          </div>
        ) : (
          <div className="intake-actions">
            <button type="button" className="btn-secondary" onClick={() => setConfirmingStop(true)}>{SHARE_STOP_LABEL}</button>
            <button type="button" className="btn-primary" onClick={onBack}>{SHARE_BACK_LABEL}</button>
          </div>
        )}
      </div>
    )
  }

  return (
    <form className="intake-share" onSubmit={submit} noValidate>
      <h1 className="triage-question">{SHARE_TITLE(advisor)}</h1>
      <p className="triage-hint">{SHARE_NOTHING_YET}</p>
      <p className="triage-hint">{SHARE_SCOPE_LINE}</p>

      <div className="intake-share-fields">
        <label className="intake-share-label" htmlFor="intake-share-name">{SHARE_NAME_LABEL}</label>
        <input
          id="intake-share-name"
          type="text"
          className="app-input"
          maxLength={SHARE_NAME_MAX}
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="intake-share-label" htmlFor="intake-share-phone">{SHARE_PHONE_LABEL}</label>
        <div className="tr-connect-phone-row">
          <select
            className="app-input tr-connect-phone-select"
            aria-label="Phone country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {PHONE_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.dialCode ? [c.flag, c.dialCode].filter(Boolean).join(' ') : c.name}
              </option>
            ))}
          </select>
          <input
            id="intake-share-phone"
            type="tel"
            className="app-input"
            placeholder={getCountry(country).placeholder}
            value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {!phoneCheck.ok && <p className="triage-email-error">{phoneCheck.error}</p>}

        <label className="intake-share-label" htmlFor="intake-share-email">{SHARE_EMAIL_LABEL}</label>
        <input
          id="intake-share-email"
          type="email"
          className="app-input intake-share-email"
          value={email || ''}
          readOnly
          disabled
        />
        <p className="u-hint">{SHARE_EMAIL_LOCKED_HINT}</p>
      </div>

      <p className="tr-connect-consent">
        {shareConsentSentence(advisor)}{' '}
        <a href="/privacy" className="disclaimer-link">{SHARE_PRIVACY_LINK_LABEL}</a>.
      </p>

      {errorText && <p className="intake-limit-note" role="alert">{errorText}</p>}

      <div className="intake-actions">
        <button type="submit" className="btn-primary" disabled={!canShare}>
          {sending ? SHARE_SENDING_LABEL : SHARE_ENTRY_LABEL(advisor)}
        </button>
        <button type="button" className="btn-secondary" disabled={sending} onClick={onBack}>{SHARE_BACK_LABEL}</button>
      </div>
    </form>
  )
}
